import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import { getToolInOut } from "features/ToolInOuts/services/ToolInOutService";
import { ToolInOut } from "features/ToolInOuts/types/tool-in-out.types";
import { getDocumentTitle } from "utils/helpers";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { ToolInOutCreateModal } from "./ToolInOutCreateModal";

/**
 * Deep-link entry for editing a tool in/out row; uses the same modal as the
 * list (same shape as `ProductCategories/ProductCategoryForm/index.tsx`).
 *
 * Reachable when `dateIn == null`, or for any status when the actor is Super
 * Admin (post-return editing) — the list's Edit action follows the same
 * rule, and this route re-checks it so a returned row can't be reached by
 * navigating here directly either, for anyone else.
 *
 * NOTE: `listPath` below is a placeholder guess (routes/sidebar are wired up
 * by a separate process per the task boundary) — only used for the
 * breadcrumb/back-navigation target and safe to adjust once the real route
 * is registered.
 */
const ToolInOutForm = () => {
  document.title = getDocumentTitle("Edit tool in/out");
  const navigate = useNavigate();
  const { id } = useParams();
  const { isSuperAdmin } = useProfile();
  const [record, setRecord] = useState<ToolInOut | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(true);

  const listPath = "/inventory-management/tool-in-outs";

  useEffect(() => {
    if (!id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRecord(true);
      try {
        const res = await getToolInOut(Number(id));
        const isOpen = res.data?.dateIn == null;
        if (!cancelled && res.success && res.data && (isOpen || isSuperAdmin)) {
          setRecord(res.data);
        } else if (!cancelled) {
          toast.error(
            res.success && res.data?.dateIn != null
              ? "This tool has already been returned and can no longer be edited."
              : "Could not load record",
          );
          navigate(listPath, { replace: true });
        }
      } catch (err) {
        console.warn(err);
        if (!cancelled) {
          toast.error("Could not load record");
          navigate(listPath, { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, isSuperAdmin]);

  const handleClose = () => {
    navigate(listPath, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Edit tool in/out"
          pageTitle="Inventory Management"
          backTo={{ label: "Tool in/out", path: listPath }}
        />
        {loadingRecord ? (
          <p className="text-muted">Loading…</p>
        ) : record ? (
          <ToolInOutCreateModal
            show
            editingRecord={record}
            onClose={handleClose}
            onSuccess={handleClose}
          />
        ) : null}
      </Container>
    </div>
  );
};

export default ToolInOutForm;
